/**
 * Lightweight, dependency-free scroll animations.
 *
 * Uses IntersectionObserver + CSS transitions instead of an animation library,
 * so nothing extra is downloaded and LCP stays untouched.
 * Respects prefers-reduced-motion.
 */

function reduced() {
  return (
    typeof window === "undefined" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function revealOnScroll(
  el: HTMLElement,
  onEnter: () => void,
  start = 0.15,
) {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onEnter();
          io.disconnect();
        }
      });
    },
    { rootMargin: `0px 0px -${Math.round(start * 100)}% 0px`, threshold: 0 },
  );
  io.observe(el);
}

function prep(el: HTMLElement, y = 18) {
  el.style.opacity = "0";
  el.style.transform = `translateY(${y}px)`;
  el.style.willChange = "opacity, transform";
}

function play(el: HTMLElement, delay = 0, duration = 0.7) {
  el.style.transition = `opacity ${duration}s ease-out ${delay}s, transform ${duration}s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`;
  el.style.opacity = "1";
  el.style.transform = "translateY(0)";
  window.setTimeout(
    () => {
      el.style.willChange = "";
      el.style.transition = "";
      el.style.transform = "";
    },
    (delay + duration) * 1000 + 60,
  );
}

/**
 * Section reveals ([data-reveal]) plus the hand-drawn grading underline draw
 * ([data-grade-underline] path), both triggered on scroll.
 */
export async function initSignatureAnimation() {
  if (reduced()) return;

  document.querySelectorAll<HTMLElement>("[data-reveal]").forEach((el) => {
    prep(el);
    revealOnScroll(el, () => play(el));
  });

  document
    .querySelectorAll<SVGPathElement>("[data-grade-underline] path")
    .forEach((path) => {
      const length = path.getTotalLength();
      path.style.strokeDasharray = String(length);
      path.style.strokeDashoffset = String(length);
      const host = path.closest("[data-grade-underline]") as HTMLElement | null;
      revealOnScroll(host ?? (path as unknown as HTMLElement), () => {
        path.style.transition = "stroke-dashoffset 1.1s ease-in-out";
        path.style.strokeDashoffset = "0";
      }, 0.2);
    });
}

/**
 * Homepage choreography: hero entrance on mount, staggered grid reveals,
 * and a gentle scroll parallax.
 */
export async function initHomeAnimations() {
  if (reduced()) return;

  // Hero entrance — plays immediately, above the fold.
  const heroItems = Array.from(
    document.querySelectorAll<HTMLElement>("[data-hero-item]"),
  );
  heroItems.forEach((el, i) => {
    prep(el, 24);
    requestAnimationFrame(() => play(el, 0.05 + i * 0.09, 0.9));
  });

  // Staggered reveals for grids / lists.
  document
    .querySelectorAll<HTMLElement>("[data-reveal-stagger]")
    .forEach((group) => {
      const items = Array.from(
        group.querySelectorAll<HTMLElement>("[data-reveal-item]"),
      );
      if (!items.length) return;
      items.forEach((el) => prep(el, 22));
      revealOnScroll(group, () => {
        items.forEach((el, i) => play(el, i * 0.08));
      }, 0.18);
    });

  // Subtle parallax on scroll.
  const parallaxEls = Array.from(
    document.querySelectorAll<HTMLElement>("[data-parallax]"),
  );
  if (parallaxEls.length) {
    let frame = 0;
    const update = () => {
      frame = 0;
      const vh = window.innerHeight;
      parallaxEls.forEach((el) => {
        const amount = Number(el.dataset.parallax || "40") / 10;
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        const progress = (rect.top + rect.height / 2) / (vh + rect.height / 2);
        const offset = (0.5 - progress) * 2 * amount;
        el.style.transform = `translate3d(0, ${offset}%, 0)`;
      });
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
  }
}
